import { z } from 'zod';
import { MoneyWireSchema } from './money.js';
import { InstallmentPlanSchema, WaiverGround } from './waiver.js';
/**
 * Identity, credit & deposit-decision contract (E-SF05) — the shape US-Shop's
 * CreditPage sends and the shape US-Enrollment-BFF's `POST /bff/credit/check`
 * emits.
 *
 * ── THE ONE RULE THAT GOVERNS THIS FILE: SSN GOES UP, NEVER COMES BACK ──────
 * The REQUEST carries name/DOB and at least one government identifier — an
 * SSN/ITIN, a driver licence, or both (the highest PII tier). The RESPONSE carries
 * the decision BRANCH and nothing that could identify the person: no SSN, no
 * DOB, no raw FICO score, no adverse-action reason code (that is operator-only,
 * FCRA-governed, delivered by Notification — not on a browser JSON), no card
 * data. The BFF's boot-time response-schema guard now forbids `ssn`/`dob`/
 * `dateOfBirth`/PAN on ANY response, so a future edit that leaks one cannot boot.
 * This schema is written to already satisfy that guard.
 *
 * The BFF holds no database. The SSN transits the BFF to Accounts over TLS,
 * server-side, and is never persisted at the edge, never logged (Pino
 * redaction), and never placed in a query key, URL, localStorage, or error.
 */
/** SSN vs ITIN — drives the ITIN deposit path in Accounts' decision engine. */
export const IdentityKind = z.enum(['SSN', 'ITIN']);
/** 9 digits, no separators. The FE strips formatting before sending. */
export const SsnSchema = z.string().regex(/^\d{9}$/u, 'ssn must be 9 digits');
/**
 * The US jurisdictions that issue a driver license or a non-driver state ID —
 * the 50 states plus the District of Columbia.
 *
 * ⚠️ TERRITORIES ARE DELIBERATELY ABSENT (PR, GU, VI, AS, MP), as are the armed-
 * forces "states" (AA/AE/AP, which are ZIP designations and issue no licence at
 * all). This is a TEXAS retail-electric storefront: the premise is in ERCOT, and
 * the field exists to name the document in the applicant's hand. Adding a
 * jurisdiction is a one-line change if a real applicant ever presents one; the
 * closed list is what stops a free-text "state" reaching an operator's screen.
 */
export const US_STATE_CODES = [
    'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'DC', 'FL', 'GA', 'HI', 'ID',
    'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO',
    'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA',
    'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
];
export const UsStateCode = z.enum(US_STATE_CODES);
/**
 * A driver license / state ID number: 4–20 uppercase letters and digits.
 *
 * ⚠️ NOT PER-STATE VALIDATED, ON PURPOSE. The 51 formats genuinely differ —
 * Texas is 8 digits, California is a letter then 7 digits, New York issues both
 * a 9-digit and an 18-character form — and they are changed by the issuing
 * agency without notice to us. A per-state regex table would therefore fail
 * CLOSED on a valid, freshly-issued licence, refusing a real customer on the
 * strength of a table nobody here maintains. The bound checks what we can
 * actually know: a plausible length, and no separators or punctuation.
 *
 * The FE uppercases and strips separators as the customer types, so a licence
 * copied as `12 345 678` arrives as `12345678` rather than being refused.
 */
export const DriversLicenseNumberSchema = z
    .string()
    .regex(/^[A-Z0-9]{4,20}$/u, 'driversLicense.number must be 4-20 letters or digits');
/**
 * TEXAS: exactly 8 digits, nothing else (Texas DPS).
 *
 * ⚠️ THIS IS THE ONE JURISDICTION WE ENCODE, AND THAT IS DELIBERATE — it is not
 * the first row of a 51-state table. The reasoning on
 * `DriversLicenseNumberSchema` above still holds for everywhere else: those
 * formats differ, change without notice to us, and a guessed rule fails CLOSED on
 * a real customer's valid licence. Texas is different only because this is a
 * Texas storefront, it is the overwhelming majority of applicants, and we have
 * the authoritative rule and can maintain it.
 *
 * ⚠️ 7-DIGIT LEGACY LICENCES ARE NOT REJECTED — they are LEFT-PADDED to 8 by the
 * caller before they get here (`normalizeTexasLicenseNumber`). This regex is the
 * canonical wire format, so by the time a value reaches it the padding has
 * happened; a bare 7 digits arriving here means a caller skipped that step, which
 * is exactly what it should catch.
 */
export const TEXAS_LICENSE_NUMBER_RE = /^\d{8}$/u;
/**
 * Left-pad a 7-digit legacy Texas licence to the modern 8-digit form.
 *
 * Exported from the contract rather than reimplemented on each side, for the same
 * reason `normalizeEmail` is: the FE pads before sending and the BFF validates
 * what arrives, and two copies of a padding rule is how they drift into
 * disagreeing about whether `1234567` is a licence.
 *
 * Anything that is not exactly 7 digits is returned untouched — this normalises,
 * it does not validate.
 */
export function normalizeTexasLicenseNumber(raw) {
    return /^\d{7}$/u.test(raw) ? `0${raw}` : raw;
}
/**
 * A US driver license or state-issued ID card.
 *
 * ⚠️ GOVERNMENT-ID PII, ONE TIER BELOW THE SSN. Like the SSN it appears ONLY on
 * this request type and on NO response type anywhere in the contract, and
 * `response-guard.ts` refuses boot if a response ever declares it.
 *
 * The number is checked twice: the general bound above (a plausible length, no
 * punctuation) for every jurisdiction, then the Texas rule for Texas only.
 */
export const DriversLicenseSchema = z
    .object({
    number: DriversLicenseNumberSchema,
    /** The issuing jurisdiction — a licence number is meaningless without it. */
    issuingState: UsStateCode,
})
    .superRefine((license, ctx) => {
    if (license.issuingState === 'TX' && !TEXAS_LICENSE_NUMBER_RE.test(license.number)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['number'],
            message: 'a Texas driver license number is exactly 8 digits',
        });
    }
});
/**
 * The identity FIELDS, before the at-least-one rule below is applied.
 *
 * Exported separately because `.superRefine` produces a `ZodEffects`, which has
 * no `.shape` — and US-Shop's `credit.service` validates individual fields
 * against this schema so the FE cannot drift from what the BFF will accept.
 */
export const CreditIdentityFieldsSchema = z.object({
    firstName: z.string().min(1).max(80),
    lastName: z.string().min(1).max(80),
    /** YYYY-MM-DD. Bounded to a plausible adult DOB range is enforced FE-side. */
    dateOfBirth: z.string().regex(/^\d{4}-\d{2}-\d{2}$/u, 'dateOfBirth must be YYYY-MM-DD'),
    /**
     * 9 digits, no separators. Highest PII tier — request-only, never echoed.
     *
     * ⚠️ OPTIONAL SINCE THE DRIVER-LICENCE OPTION LANDED, AND THAT IS A LOOSENING,
     * NOT A WEAKENING. The applicant may now identify themselves with an SSN/ITIN,
     * a driver licence, or both, and `CreditIdentitySchema` below refuses a request
     * carrying NEITHER. Optionality here is also what keeps a cached SPA bundle
     * that predates the licence fields working unchanged: it always sends an `ssn`,
     * and an always-present field cannot be broken by being made optional.
     */
    ssn: SsnSchema.optional(),
    /** The driver licence / state ID alternative. See `DriversLicenseSchema`. */
    driversLicense: DriversLicenseSchema.optional(),
    identityKind: IdentityKind.default('SSN'),
});
/**
 * Consumer identity for the credit pull — at least one identifier, either kind.
 *
 * ── WHY "AT LEAST ONE" AND NOT "EXACTLY ONE" ───────────────────────────────
 * Both documents identify the same person and they corroborate rather than
 * contradict each other, so an applicant who supplies both has given us MORE to
 * work with, not an ambiguity to resolve. Refusing that would be refusing a
 * strictly better-evidenced application.
 *
 * ── WHAT EACH ONE CAN ACTUALLY DO TODAY ────────────────────────────────────
 * ⚠️ Only the SSN/ITIN reaches the bureau. Accounts' credit-decision endpoint
 * validates `identityKind: z.enum(['SSN','ITIN'])` on a `.strict()` object, so a
 * licence number has nowhere to go in that call. `POST /bff/credit/check`
 * therefore refuses a licence-ONLY request with
 * `PROBLEM_TYPES.identityNumberRequired` and says so plainly, rather than
 * fabricating a decision or letting Accounts 400 arrive as a bare 500. The
 * licence is still captured (it identifies the applicant for the operator and
 * the at-accept saga); it is the bureau pull specifically that needs the SSN.
 * When Accounts learns the kind, that refusal is the only thing that changes.
 */
export const CreditIdentitySchema = CreditIdentityFieldsSchema.superRefine((identity, ctx) => {
    if (identity.ssn === undefined && identity.driversLicense === undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            // Anchored on `ssn` so a form binding field errors by path still has
            // somewhere to put it; the message names both routes forward.
            path: ['ssn'],
            message: 'provide a Social Security Number/ITIN, a driver license, or both',
        });
    }
});
/**
 * Customer CONTACT details (E-SF06 blocker 1). Explicit + REQUIRED for
 * enrollment: Accounts' account-create needs an email + phone, and the funnel
 * never captured them (the credit step collected only name/DOB/SSN). The BFF
 * writes these into the funnel SESSION at the credit step and carries them to
 * Accounts ONLY at sign (the at-accept saga passes them to the create endpoint).
 *
 * ── CONTACT PII, NOT THE SSN TIER — BUT STILL NEVER LEAKED ──────────────────
 * These are a LOWER PII tier than SSN/DOB, so they are NOT in the response
 * boot-guard's forbidden-field set (which stays as-is). But they are still PII:
 * they appear ONLY on this request type and on NO response type; the BFF does
 * not forward them to Accounts at the credit step; and Pino redaction already
 * covers `email`/`phone` at every depth on the request path. They are never
 * echoed, cached in a query key, or logged.
 */
export const CreditContactSchema = z.object({
    /** RFC5321-ish; the BFF stores it verbatim in the session and never echoes it. */
    email: z.string().email().max(254),
    /** NANP: exactly 10 digits — the FE strips separators before sending. */
    phone: z.string().regex(/^\d{10}$/u, 'phone must be 10 digits'),
});
/**
 * The version stamp recorded alongside an accepted information-accuracy
 * undertaking.
 *
 * ⚠️ PLACEHOLDER — THE LEGAL COPY IS NOT FINALISED (with counsel as of
 * 2026-07-28). The wording of the undertaking is therefore NOT in this package
 * and must not be invented in a schema comment; what IS here is the mechanism
 * that makes the eventual wording auditable: every acceptance is stamped with the
 * version of the text the customer actually saw. When counsel signs off, bump this
 * constant in the SAME change that lands the copy, so an acceptance recorded
 * against `…-draft` can never be mistaken for one recorded against the final
 * text. A version-less acceptance is an acceptance of nothing in particular.
 */
export const UNDERTAKING_VERSION_PLACEHOLDER = 'us-information-accuracy-draft-0';
/**
 * Explicit consent for the credit pull. The two regulated flags are
 * `literal(true)`: the pull is a regulated action and cannot proceed without an
 * affirmative FCRA authorization and an acknowledgement of the PUCT §25.478
 * deposit disclosure. Making them `literal(true)` means "credit checked without
 * consent" is un-representable.
 */
export const CreditConsentSchema = z.object({
    /** FCRA §604 — authorization to obtain a consumer report for this enrollment. */
    fcraAuthorization: z.literal(true),
    /** PUCT §25.478 — acknowledgement that a deposit may be required. */
    puctDepositDisclosure: z.literal(true),
    /**
     * The customer's undertaking that the information they supplied is accurate.
     *
     * ⚠️ OPTIONAL IN THIS RELEASE, AND THAT IS THE WHOLE DESIGN — DO NOT "TIDY" IT
     * INTO A REQUIRED FIELD IN A PATCH. The SPA is a cached static bundle served
     * from an ingress; at any moment after a deploy there are browsers running the
     * PREVIOUS bundle, which knows nothing about this field. A required field would
     * make the BFF `400` every one of those in-flight enrollments — a total outage
     * for anyone who had the page open, visible to us only as a spike in 400s on the
     * single most consequential PII endpoint in the funnel.
     *
     * So the rollout is two releases, deliberately:
     *   1. THIS release — `.optional()`. The BFF records it when present and does
     *      not require it. Old bundles keep working; new bundles start sending it.
     *   2. A LATER release, once the legal copy is final AND no cached bundle can
     *      still be omitting it — flip to the `literal(true)` idiom the two flags
     *      above use, i.e. `informationAccuracyUndertaking: z.literal(true)`, so
     *      "credit checked without an undertaking" becomes un-representable exactly
     *      like "credit checked without FCRA authorization" already is.
     *
     * It is `z.boolean()` and not `z.literal(true)` today for the same reason: a
     * `literal(true).optional()` would `400` a new bundle that faithfully reports an
     * UNCHECKED box as `false`, which is a correct thing for a client to say. The
     * BFF treats only `=== true` as an affirmative undertaking.
     */
    informationAccuracyUndertaking: z.boolean().optional(),
    /**
     * The version of the undertaking copy the customer was shown — see
     * `UNDERTAKING_VERSION_PLACEHOLDER`. `.optional()` for the same
     * cached-bundle reason as the flag above, and it becomes required in the same
     * later release.
     */
    undertakingVersion: z.string().min(1).max(64).optional(),
});
/**
 * `POST /bff/credit/check` request.
 *
 * `candidateId` is the OPAQUE, session-scoped premise handle from E-SF03 — the
 * BFF redeems it server-side to the resolved ESI (and derives the projected bill
 * for the deposit amount). The browser never holds the ESIID; it holds the
 * handle. `planId` is the shopped tariff.
 *
 * `contact` (email + phone) is captured here so the at-accept saga (E-SF06) can
 * build the Accounts account at sign — see `CreditContactSchema`. It is written
 * to the funnel session by the BFF credit route and is NOT forwarded to Accounts
 * at this step.
 */
export const CreditCheckRequestSchema = z.object({
    candidateId: z.string().min(1),
    planId: z.string().min(1).max(255),
    identity: CreditIdentitySchema,
    contact: CreditContactSchema,
    consent: CreditConsentSchema,
});
/**
 * The deposit branch's customer-facing payload.
 *
 * Carries the AMOUNT (versioned money), the due date, and the §25.478 waiver
 * grounds OFFERED for this deposit (first-class — see waiver.ts). It does NOT
 * carry the FCRA adverse-action reason code: that is operator-only and reaches
 * the customer through the statutory adverse-action notice (Notification /
 * E-NT01), never this JSON.
 */
export const DepositBranchSchema = z.object({
    amount: MoneyWireSchema,
    /** ISO-8601 date the deposit is due (server-computed, Texas business days). */
    dueBy: z.string().min(1),
    /** The §25.478 grounds the customer may elect instead of paying (AC1). */
    offeredWaivers: z.array(WaiverGround).min(1),
    /** The statutory two-installment terms, surfaced when low-income split is offered. */
    installmentOption: InstallmentPlanSchema.optional(),
});
/**
 * The credit decision — the THREE BRANCHES (product-owner LOCKED, E-AC01):
 * approve / deposit / waived, as a discriminated union so the SPA renders exactly
 * one and cannot conflate them.
 *
 *   - `approve`  — no deposit; the customer proceeds to sign (E-SF06).
 *   - `deposit`  — a deposit is required; pay it OR elect a §25.478 waiver (AC2).
 *   - `waived`   — a statutory PUCT §25.478(e) waiver ground applied at decision
 *                  time (65+/medical/family-violence), so no deposit is owed. The
 *                  branch carries only the ground id — never PII.
 *
 * ⚠️ Prepaid is DEFERRED — there is deliberately NO `prepaid` branch (E-AC01's
 * `ProspectCreditOutcome` locked the model to approve/deposit/waived). The
 * Accounts engine (`computeDecision`) emits `approve`/`deposit` today and grows a
 * statutory `waived` ground later; the branch is typed and handled now so the
 * wired client drops in unchanged when it becomes reachable.
 *
 * ⚠️ NO `score`, NO `ssn`, NO `reason` field anywhere in this union.
 */
export const CreditDecisionResponseSchema = z.discriminatedUnion('outcome', [
    z.object({ outcome: z.literal('approve') }),
    z.object({
        outcome: z.literal('deposit'),
        deposit: DepositBranchSchema,
    }),
    z.object({
        outcome: z.literal('waived'),
        waived: z.object({
            /**
             * The statutory PUCT §25.478(e) waiver ground id passed through from
             * Accounts' `ProspectCreditOutcome.waiverGroundId` (e.g. `SIXTY_FIVE_PLUS`).
             * Operator-neutral, non-PII — safe to render.
             */
            waiverGroundId: z.string().min(1).max(64),
        }),
    }),
]);
//# sourceMappingURL=credit.js.map